import React from 'react';

function DateRatingWidget({ booking, matchFirstName, onSubmit }) {
  const [overallRating, setOverallRating] = React.useState(0);
  const [hoveredOverall, setHoveredOverall] = React.useState(0);
  const [pricePerPerson, setPricePerPerson] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const hasVenue = booking.venue?.name;
  const isMultiStop = booking.venue?.isMultiStop && booking.venue?.stops?.length > 0;
  const venues = isMultiStop 
    ? booking.venue.stops.filter(s => s.venue).map(s => s.venue)
    : hasVenue ? [booking.venue.name] : [];

  // venueRatings: { venueName: rating } for multi-stop, or single number for single venue
  const [venueRatings, setVenueRatings] = React.useState({});
  const [hoveredVenues, setHoveredVenues] = React.useState({});

  const StarRow = ({ value, hovered, onSelect, onHover, onLeave, size = '28px' }) => (
    <div style={{ display: 'flex', gap: '4px' }}>
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          onMouseEnter={() => onHover(star)}
          onMouseLeave={() => onLeave()}
          onClick={() => onSelect(star)}
          style={{
            fontSize: size,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            filter: star <= (hovered || value) ? 'none' : 'grayscale(1) opacity(0.35)',
            transform: star <= (hovered || value) ? 'scale(1.1)' : 'scale(1)',
            transition: 'all 0.15s ease'
          }}
        >
          ⭐
        </button>
      ))}
    </div>
  );

  const handleSubmit = async () => {
    if (overallRating === 0) return;
    setSubmitting(true);
    // For single venue, send as number. For multi-stop, send as object.
    const venueRatingData = venues.length === 1 
      ? (venueRatings[venues[0]] || undefined)
      : (Object.keys(venueRatings).length > 0 ? venueRatings : undefined);
    await onSubmit(overallRating, venueRatingData, pricePerPerson || undefined, comment);
    setSubmitting(false);
  };

  return (
    <div>
      {/* Overall Rating */}
      <div style={{ marginBottom: '14px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: '#2c3e50' }}>
          How was your date with {matchFirstName}?
        </div>
        <StarRow
          value={overallRating}
          hovered={hoveredOverall}
          onSelect={setOverallRating}
          onHover={setHoveredOverall}
          onLeave={() => setHoveredOverall(0)}
        />
      </div>

      {overallRating > 0 && (
        <>
          {/* Venue Ratings */}
          {venues.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#2c3e50' }}>
                📍 {venues.length === 1 ? 'How was the venue?' : 'Rate the venues'} <span style={{ fontWeight: 400, color: '#95a5a6' }}>(optional)</span>
              </div>
              {venues.map((venueName, idx) => (
                <div key={idx} style={{ marginBottom: venues.length > 1 ? '8px' : '0' }}>
                  {venues.length > 1 && (
                    <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>
                      {venueName}
                    </div>
                  )}
                  {venues.length === 1 && (
                    <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>
                      {venueName}
                    </div>
                  )}
                  <StarRow
                    value={venueRatings[venueName] || 0}
                    hovered={hoveredVenues[venueName] || 0}
                    onSelect={(star) => setVenueRatings(prev => ({ ...prev, [venueName]: star }))}
                    onHover={(star) => setHoveredVenues(prev => ({ ...prev, [venueName]: star }))}
                    onLeave={() => setHoveredVenues(prev => ({ ...prev, [venueName]: 0 }))}
                    size="24px"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Price Per Person */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#2c3e50' }}>
              💰 Approx. spend per person? <span style={{ fontWeight: 400, color: '#95a5a6' }}>(optional)</span>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[
                { value: 'free', label: 'Free' },
                { value: '1-25', label: '$1–25' },
                { value: '26-50', label: '$26–50' },
                { value: '51-100', label: '$51–100' },
                { value: '100+', label: '$100+' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPricePerPerson(pricePerPerson === opt.value ? '' : opt.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '16px',
                    border: pricePerPerson === opt.value ? '2px solid #764ba2' : '1px solid #ddd',
                    background: pricePerPerson === opt.value ? 'linear-gradient(135deg, #667eea20, #764ba220)' : 'white',
                    color: pricePerPerson === opt.value ? '#764ba2' : '#555',
                    fontSize: '12px',
                    fontWeight: pricePerPerson === opt.value ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <textarea
            placeholder="Any comments? (Optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              fontSize: '13px',
              resize: 'none',
              fontFamily: 'inherit',
              marginBottom: '10px',
              boxSizing: 'border-box'
            }}
          />

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '10px 20px',
              background: submitting ? '#95a5a6' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              width: '100%'
            }}
          >
            {submitting ? 'Submitting...' : 'Submit & Archive'}
          </button>
        </>
      )}
    </div>
  );
}

export default DateRatingWidget;
